import { useMemo } from "react";
import {useSlims} from "../hooks.js";
import  {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable
  } from "react-table";
  
import Link from "../widgets/Link.jsx";
import { HeaderWithModal } from "../utils.jsx"

const columnHelper = createColumnHelper()

const SlimsTile = ({protein_seq_id}) => {

    const {isReady, matches} = useSlims(protein_seq_id)

    // is_valid marks motifs that fall inside an intrinsically disordered region.
    const inIdr = d => d.is_valid === true || d.is_valid === 1 || d.is_valid === '1' || d.is_valid === 'True'
    const sorted = useMemo(() =>
        [...matches].sort((a, b) => (inIdr(b) - inIdr(a)) || (a.slim_start - b.slim_start)),
        [matches]);

    const columns = useMemo(() => [
        columnHelper.accessor(
          row => row,
          {
            id: "ELM id",
            header: () => <span>ELM id</span>,
            cell: info => {
                const d = info.getValue()
                if (d) {
                  return <Link target={"_blank"}  href={`http://elm.eu.org/elms/${d.elm_id}`}  key={`tt-${d.elm_id}`}  text={d.elm_id}  extraClasses={"mx-1 font-medium text-blue-600"} />
                }
            }
          }
        ),
        columnHelper.accessor(
            row => row,
            {
              id: "motif sequence",
              header: () => <span>motif sequence</span>,
              cell: info => <span className="font-mono">{info.getValue().slim_seq}</span>
            }
        ),
        columnHelper.accessor(
            row => row,
            {
              id: "description",
              header: () => <span>description</span>,
              cell: info => info.getValue().Description
            }
        ),
        columnHelper.accessor(
            row => row,
            {
              id: "position",
              header: () => <span>position</span>,
              cell: info => {
                const d = info.getValue()
                  return <span className="whitespace-nowrap font-mono tabular-nums">{d.slim_start} - {d.slim_end}</span>
              }
            }
        ),
        columnHelper.accessor(
          row => row,
          {
            id: "score",
            header: () => <span>score</span>,
            cell: info => <span className="whitespace-nowrap font-mono tabular-nums">{info.getValue().Probability.toExponential(2)}</span>
          }
      ),
      columnHelper.accessor(
        row => row,
        {
          id: "in IDR",
          header: () => <span>in IDR</span>,
          cell: info => {
            const d = info.getValue()
            return inIdr(d)
              ? <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">in IDR</span>
              : <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">outside IDR</span>
          }
        }
      )
      ], [])

    const table = useReactTable({
        data: sorted,
        columns,
        getCoreRowModel: getCoreRowModel()
    })

    const nInIdr = useMemo(() => sorted.filter(inIdr).length, [sorted])

    if(!isReady) {
        return <span>...loading</span>
    }

    if(!matches || matches.length==0)  {
      return (
        <div className="shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] mt-3 mr-3 rounded p-3">
            <div className="border-b width-full border-slate-200">
                <p className="font-semibold">Predicted Short Linear Motifs by ELM</p>
            </div>
            <div>no detected SLIMs in this sequence</div>
        </div>
      );
    }

    return (
      <div className="shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] mt-3 mr-3 rounded p-3">
          <div className="border-b width-full border-slate-200">
            <HeaderWithModal title="Predicted Short Linear Motifs by ELM">
            <p>SLIM Prediction by ELM. 
            <a className="cursor-pointer hover:underline text-blue-600" href='/documentation'> See the documentation</a> for more details on the method.</p>
            <p>ELM developers warn against a probable high false positive rate. Context is important when interpreting the result. <a className="cursor-pointer hover:underline text-blue-600" href='http://elm.eu.org/help.html'>See the ELM help page for more details.</a></p>
            <table className="border-collapse border-t border-slate-400" >
              <tr className="border-t border-slate-300">
                <th className="pr-10 py-2 pl-1 rounded-lg text-gray-700">Column</th>
                <th className="pr-10 py-2 pl-1 rounded-lg text-gray-700">Description</th>
              </tr>
              <tr className="border-t border-slate-300">
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">ELM ID</td>
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">The ID of the ELM class detected, click to view details on ELM website.</td>
              </tr>
              <tr className="border-t border-slate-300">
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">Motif Sequence </td>
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">The motif detected on the protein that fits the regular expression.</td>
              </tr>
              <tr className="border-t border-slate-300">
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">Description </td>
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">A short description of the ELM class.</td>
              </tr>
              <tr className="border-t border-slate-300">
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">Position </td>
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">The start and end positions of the motif on the amino acid sequence.</td>
              </tr>
              <tr className="border-t border-slate-300">
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">Score </td>
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">Probability of finding the motif on a random amino acid sequence. A lower score means a lower chance of false positive.  <a className="cursor-pointer hover:underline text-blue-600" href='http://elm.eu.org/help.html#filter_probability'>See the ELM help page for more details.</a></td>
              </tr>
              <tr className="border-t border-slate-300">
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">In IDR </td>
                <td className="pr-10 py-2 pl-1 rounded-lg text-gray-700">True when the motif falls inside an intrinsically disordered region (IDR). SLiMs located in disordered regions are more likely to be functional.</td>
              </tr>
            </table>
            <p></p>

            </HeaderWithModal>
          </div>
        <div className="mb-2 text-sm text-slate-500">
            {sorted.length} predicted motif{sorted.length === 1 ? '' : 's'} · {nInIdr} inside an IDR
        </div>
        <div className="overflow-hidden rounded border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className="border-b border-slate-200">
                {headerGroup.headers.map(header => (
                  <th key={header.id} className={`px-3 py-2 text-xs font-medium uppercase tracking-wide ${header.id === 'score' ? 'text-right' : 'text-left'}`}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id} className="border-b border-slate-100 last:border-0 bg-white hover:bg-slate-50">
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className={`px-3 py-2 ${cell.column.id === 'score' ? 'text-right' : 'text-left'}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
}

export default SlimsTile